import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL - handles both old and new formats
const getImageUrl = (url) => {
  if (!url) return '';
  // Already a full URL
  if (url.startsWith('http')) {
    // Fix old format: if URL contains /uploads/ without /api/, add /api
    if (url.includes('/uploads/') && !url.includes('/api/uploads/')) {
      return url.replace('/uploads/', '/api/uploads/');
    }
    return url;
  }
  // Relative path - add BACKEND_URL
  // If path doesn't start with /api, add it
  if (url.startsWith('/uploads/')) {
    return `${BACKEND_URL}/api${url}`;
  }
  return `${BACKEND_URL}${url}`;
};

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [recipes, setRecipes] = useState([]);

  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [productsRef, productsInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [recipesRef, recipesInView] = useInView({ triggerOnce: true, threshold: 0.1 });

  useEffect(() => {
    fetchProducts();
    fetchRecipes();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?featured=true`);
      setProducts(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes`);
      setRecipes(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching recipes:', error);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1915]">
      <Navigation />

      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden stone-texture"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url(./images/bg_home.webp)',
            filter: 'brightness(0.5)'
          }}
        />

        <div className={`relative z-10 text-left px-6 lg:px-12 max-w-7xl mx-auto w-full ${heroInView ? 'fade-in-up' : 'opacity-0'}`}>
          <div className="mb-8">
            <span className="inline-block px-6 py-2 border-2 border-[#C9A961] text-[#C9A961] text-sm font-medium tracking-widest uppercase">
              Extra Virgin Excellence
            </span>
          </div>

          <h1 className="playfair text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            Legato d'Oro
          </h1>

          <p className="playfair text-2xl sm:text-3xl lg:text-4xl text-[#C9A961] mb-8 font-light italic">
            The Golden Harmony from Earth to Your Table
          </p>

          <p className="inter text-lg text-gray-300 mb-12 max-w-2xl leading-relaxed">
            Discover 3000 years of heritage flowing in every golden drop.
            From the ancient lands of Carthage to tables worldwide.
          </p>

          <Link to="/terroir" data-testid="discover-secret-button">
            <button className="btn-gold-filled">
              Discover Our Secret
            </button>
          </Link>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <svg className="w-6 h-6 text-[#C9A961]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </section>

      {/* Notre Odyssée Section */}
      <section className="py-24 px-6 bg-[#221F1A] stone-texture">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="playfair text-4xl lg:text-5xl font-bold text-[#C9A961] mb-6">
              Our Odyssey
            </h2>
            <div className="w-32 h-1 bg-[#C9A961] mx-auto mb-8"></div>
            <h3 className="playfair text-3xl text-white mb-6 italic">Gold Linked by Memory</h3>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-gray-300 leading-relaxed">
              <p className="text-lg">
                Our olive trees are rooted in soil rich with over 3000 years of memory,
                where ancient civilizations viewed olives as symbols of wealth, light, and prosperity.
              </p>
              <p className="text-lg">
                The elephant—our emblem—represents strength, intelligence, and loyalty,
                transcending borders just as our oil connects the power of the past with
                the excellence of the present.
              </p>
              <p className="text-lg">
                Like the musical term "legato" that connects notes fluidly, Legato d'Oro
                links Carthaginian tradition with contemporary refinement, the Tunisian
                land with tables worldwide.
              </p>
            </div>

            <div className="relative">
              <div className="aspect-square rounded-lg overflow-hidden shadow-2xl">
                <img
                  src="./images/odessey.webp"
                  alt="Legato d'Oro - Carthaginian Heritage"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section
        ref={productsRef}
        className="py-24 px-6 bg-[#1A1915] stone-texture"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="playfair text-4xl lg:text-5xl font-bold text-[#C9A961] mb-6">
              Our Premium Collection
            </h2>
            <div className="w-32 h-1 bg-[#C9A961] mx-auto mb-8"></div>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Exceptional oils, each bottle a symbol of strength and continuity
            </p>
          </div>

          <div className={`grid md:grid-cols-3 gap-8 ${productsInView ? 'fade-in' : 'opacity-0'}`}>
            {products.length > 0 ? (
              products.map((product, index) => (
                <div
                  key={product.id}
                  data-testid={`product-card-${index}`}
                  className="group bg-[#2A2720] rounded-lg overflow-hidden border border-[#C9A961]/20 hover:border-[#C9A961] transition-all duration-300 hover:shadow-2xl hover:shadow-[#C9A961]/20"
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={getImageUrl(product.image_url)}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <span className="text-xs text-[#C9A961] uppercase tracking-wider">
                      {product.category.replace('_', ' ')}
                    </span>
                    <h3 className="playfair text-2xl font-semibold text-white mt-2 mb-3">
                      {product.name}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                      {product.description}
                    </p>
                    <Link to="/collection">
                      <button className="btn-gold w-full" data-testid={`explore-product-${index}`}>
                        Explore
                      </button>
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              // Placeholder cards
              [1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-[#2A2720] rounded-lg overflow-hidden border border-[#C9A961]/20"
                >
                  <div className="aspect-square bg-[#3A352B]"></div>
                  <div className="p-6">
                    <div className="h-4 bg-[#3A352B] rounded mb-3 w-1/2"></div>
                    <div className="h-6 bg-[#3A352B] rounded mb-3"></div>
                    <div className="h-12 bg-[#3A352B] rounded"></div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/collection">
              <button className="btn-gold-filled" data-testid="view-full-collection">
                View Full Collection
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Recipes Section */}
      <section
        ref={recipesRef}
        className="py-24 px-6 bg-[#221F1A] stone-texture"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="playfair text-4xl lg:text-5xl font-bold text-[#C9A961] mb-6">
              The Art of Legato: Our Recipes
            </h2>
            <div className="w-32 h-1 bg-[#C9A961] mx-auto mb-8"></div>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Elevate your culinary creations with our premium olive oil
            </p>
          </div>

          <div className={`grid md:grid-cols-3 gap-8 ${recipesInView ? 'fade-in' : 'opacity-0'}`}>
            {recipes.length > 0 ? (
              recipes.map((recipe, index) => (
                <div
                  key={recipe.id}
                  data-testid={`recipe-card-${index}`}
                  className="group relative overflow-hidden rounded-lg shadow-xl hover:shadow-2xl hover:shadow-[#C9A961]/20 transition-all duration-300"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={getImageUrl(recipe.image_url)}
                      alt={recipe.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent flex items-end">
                    <div className="p-6 w-full">
                      <span className="text-xs text-[#C9A961] uppercase tracking-wider">
                        {recipe.category}
                      </span>
                      <h3 className="playfair text-xl font-semibold text-white mt-2">
                        {recipe.title}
                      </h3>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              [1, 2, 3].map((i) => (
                <div key={i} className="aspect-[4/3] bg-[#2A2720] rounded-lg"></div>
              ))
            )}
          </div>

          <div className="text-center mt-12">
            <Link to="/recipes">
              <button className="btn-gold" data-testid="view-all-recipes">
                View All Recipes
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter Section - Now integrated in Footer */}

      <Footer />
    </div>
  );
};

export default HomePage;